import type { Task } from '@shared/domain.ts';
import { agentPulse, ciPulse, type Deploy } from '@/data/selectors.ts';
import { cn, elapsed } from '@/lib/format.ts';
import { Tip } from './ui.tsx';

/**
 * The two states the whole product is about, rendered.
 *
 *   1. Is GitHub working on this branch?
 *   2. Is a routine working on it, and on what?
 *
 * They are drawn as two separate chips, side by side, never merged into one
 * "status" — because they genuinely are independent. A branch can have green CI
 * and a dead routine; a busy routine and no CI at all. Collapsing them is how
 * you end up with a board that looks fine while nothing is happening.
 *
 * A filled, pulsing dot means *now*. Everything else is a stated fact with a
 * timestamp behind it.
 */

type Variant = 'card' | 'dark';

const AGENT_TONE: Record<string, { card: string; dark: string }> = {
  live: { card: 'text-good', dark: 'text-goodx' },
  waiting: { card: 'text-amber', dark: 'text-amberx' },
  blocked: { card: 'text-warn', dark: 'text-warnx' },
  quiet: { card: 'text-carddim', dark: 'text-mute' },
  done: { card: 'text-carddim', dark: 'text-mute' },
};

const AGENT_DOT: Record<string, { card: string; dark: string }> = {
  live: { card: 'bg-good', dark: 'bg-goodx' },
  waiting: { card: 'bg-amber', dark: 'bg-amberx' },
  blocked: { card: 'bg-warn', dark: 'bg-warnx' },
  quiet: { card: 'bg-carddim/50', dark: 'bg-mute/50' },
  done: { card: 'bg-carddim/50', dark: 'bg-mute/50' },
};

export function AgentPulseChip({
  task,
  now,
  variant = 'card',
  showDetail = true,
}: {
  task: Task;
  now: number;
  variant?: Variant;
  showDetail?: boolean;
}) {
  const pulse = agentPulse(task, now);
  const tone = AGENT_TONE[pulse.tone]![variant];
  const dot = AGENT_DOT[pulse.tone]![variant];

  // While a routine is working, the useful number is how long it has been at
  // this; once it has stopped, it is how long ago that was.
  const since = pulse.live
    ? elapsed(task.agent_activity_at ?? task.agent_heartbeat_at, now)
    : elapsed(task.agent_heartbeat_at, now);

  const tip = pulse.stale
    ? `The routine has not reported for ${since}. It may have died — send the task back to restart it.`
    : pulse.live
      ? `${task.agent_activity ? `${task.agent_activity} · ` : ''}reporting for ${since}`
      : pulse.detail ?? null;

  return (
    <Tip content={tip}>
      <span className={cn('inline-flex min-w-0 items-center gap-1.5 text-[12.5px]', tone)}>
        <span
          className={cn('size-[7px] shrink-0 rounded-full', dot, pulse.live && 'rg-live')}
          aria-hidden
        />
        <span className="font-[650] whitespace-nowrap">{pulse.word}</span>
        {showDetail && pulse.live && pulse.detail ? (
          <span
            className={cn(
              'min-w-0 truncate font-normal',
              variant === 'card' ? 'text-cardmid' : 'text-soft',
            )}
          >
            {pulse.detail}
          </span>
        ) : null}
        {pulse.live && since ? (
          <span className={cn('tabular-nums', variant === 'card' ? 'text-carddim' : 'text-mute')}>
            {since}
          </span>
        ) : null}
      </span>
    </Tip>
  );
}

const CI_GLYPH = { passing: '✓', failing: '✕', running: '◐', none: '·' } as const;

const CI_TONE: Record<string, { card: string; dark: string }> = {
  passing: { card: 'text-good', dark: 'text-goodx' },
  failing: { card: 'text-warn', dark: 'text-warnx' },
  running: { card: 'text-amber', dark: 'text-amberx' },
  none: { card: 'text-carddim', dark: 'text-mute' },
};

export function CiPulseChip({
  task,
  variant = 'card',
  now,
}: {
  task: Task;
  variant?: Variant;
  now: number;
}) {
  const pulse = ciPulse(task);
  // Nothing to say yet is better said by saying nothing.
  if (pulse.tone === 'none' && !task.pr_url) return null;

  const tone = CI_TONE[pulse.tone]![variant];
  const since = task.ci_updated_at ? elapsed(task.ci_updated_at, now) : null;
  const body = (
    <span className={cn('inline-flex items-center gap-1.5 text-[12.5px] font-[650]', tone)}>
      <span className={cn('text-[11px] leading-none', pulse.live && 'rg-live')} aria-hidden>
        {CI_GLYPH[pulse.tone]}
      </span>
      <span className="whitespace-nowrap">{pulse.word}</span>
    </span>
  );

  const tip =
    pulse.tone === 'none'
      ? 'GitHub has not reported any checks on this branch yet'
      : `${pulse.detail ?? pulse.word}${since ? ` · updated ${since} ago` : ''}`;

  if (!pulse.url) return <Tip content={tip}>{body}</Tip>;

  return (
    <Tip content={tip}>
      <a
        href={pulse.url}
        target="_blank"
        rel="noreferrer"
        onClick={(event) => event.stopPropagation()}
        // No background to show for it, so the padding is invisible — it
        // only exists to grow the tap target past the bare text's height.
        className="rg-focus -my-1 rounded px-0.5 py-1 no-underline hover:underline"
      >
        {body}
      </a>
    </Tip>
  );
}

/**
 * A deploy, in the same shape as the CI chip beside it.
 *
 * `ciPulse` only ever says what GitHub's checks are doing — build, test,
 * lint, whatever a repo has wired up — because a check run genuinely does
 * not know it is a deploy. A named workflow does: this reads that instead, so
 * a row can say "deploying preview…" rather than leaving it as one more
 * "building" indistinguishable from the test suite. Same glyphs and tones as
 * `CiPulseChip` on purpose — it is still GitHub working on the branch, just a
 * more specific sentence about what kind of work.
 */
export function DeployPulseChip({
  deploy,
  now,
  variant = 'card',
}: {
  deploy: Deploy;
  now: number;
  variant?: Variant;
}) {
  if (deploy.state === 'none') return null;

  const tone = CI_TONE[deploy.state]![variant];
  const since = deploy.at ? elapsed(deploy.at, now) : null;
  const body = (
    <span className={cn('inline-flex items-center gap-1.5 text-[12.5px] font-[650]', tone)}>
      <span className={cn('text-[11px] leading-none', deploy.state === 'running' && 'rg-live')} aria-hidden>
        {CI_GLYPH[deploy.state]}
      </span>
      <span className="whitespace-nowrap">{deploy.word}</span>
    </span>
  );

  const tip = `${deploy.word}${since ? ` · ${since} ago` : ''}`;

  if (!deploy.url) return <Tip content={tip}>{body}</Tip>;

  return (
    <Tip content={tip}>
      <a
        href={deploy.url}
        target="_blank"
        rel="noreferrer"
        onClick={(event) => event.stopPropagation()}
        // No background to show for it, so the padding is invisible — it
        // only exists to grow the tap target past the bare text's height.
        className="rg-focus -my-1 rounded px-0.5 py-1 no-underline hover:underline"
      >
        {body}
      </a>
    </Tip>
  );
}

/** The pull request, and only ever in one place. */
export function PrChip({ task, variant = 'card' }: { task: Task; variant?: Variant }) {
  if (!task.pr_url) return null;
  const label = task.pr_number ? `PR #${task.pr_number}` : 'PR';
  const state = task.pr_state;

  return (
    <Tip content={state ? `Pull request is ${state}` : 'Open the pull request'}>
      <a
        href={task.pr_url}
        target="_blank"
        rel="noreferrer"
        onClick={(event) => event.stopPropagation()}
        className={cn(
          'rg-focus inline-flex items-center gap-1.5 rounded-full border px-2.5 py-[3px] text-[12px] font-semibold whitespace-nowrap no-underline hover:no-underline',
          variant === 'card'
            ? 'border-cardbtnbd bg-cardbtn text-cardbtntx hover:bg-cardbtnhover hover:text-cardhi'
            : 'border-btnbd bg-btn text-t2 hover:bg-btnhover hover:text-hi2',
          state === 'merged' && (variant === 'card' ? 'text-violet' : 'text-violetx'),
          state === 'closed' && 'opacity-60',
        )}
      >
        <GitHubMark />
        {label}
        {state === 'draft' ? <span className="font-normal opacity-70">draft</span> : null}
      </a>
    </Tip>
  );
}

export function GitHubMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden className={cn('size-3.5 fill-current', className)}>
      <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27s1.36.09 2 .27c1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8Z" />
    </svg>
  );
}
